import TextField from "../../../ui/TextField";
import Autocomplete from "../../../ui/Autocomplete";
import { Fragment, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Box, Paper, Typography, IconButton, Chip, Snackbar, Alert, Grid, LinearProgress, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SizeSoftXLayoutForm from "../../../leyout/SizeSoftXLayoutForm";
import SizeSoftXAlert from "../../../utils/SizeSoftXAlert";

import { useConteosEscaneo, playBeep } from "../../../../context/ConteosEscaneo/ConteosEscaneoContext";

import { useFormTabLock } from "../../../../hooks/useFormTabLock";
import MnuConteosEscaneoActions from "./MnuConteosEscaneoActions";
import SizeSoftXLectorCodigoBarrasConteos from "../../../leyout/SizeSoftXLectorCodigoBarrasConteos";
import SizeSoftXCodigoBarrasCamaraConteos from "../../../leyout/SizeSoftXCodigoBarrasCamaraConteos";
import SizeSoftXConfirmDialog from "../../../leyout/SizeSoftXConfirmDialog";
import ModalBuscarArticulosTomaInventario from "../ModalBuscarArticulosTomaInventario";

let tempPkCounter = 0;
let tempIdCounter = 0;

const nombreFaseCatalogo = (fase) => {
  switch (fase) {
    case "descarga": return "Descargando catélogo";
    case "procesando": return "Procesando registros";
    case "indexando": return "Indexando para escaneo";
    case "guardando": return "Guardando copia local";
    case "cache": return "Leyendo caché local";
    case "cache-lleno": return "Caché local lleno (sesión activa)";
    case "listo": return "Catálogo listo";
    default: return "Cargando catélogo";
  }
};

const crearObjetoScanPendiente = (articulo) => {
  tempPkCounter += 1;
  tempIdCounter += 1;
  const codigo = (articulo.codigobarras || "").trim();
  return {
    _temppk: `temp_${tempPkCounter}`,
    _idUnico: `pend_${tempIdCounter}`,
    _folio: "",
    _clave: (articulo.clavearticulo || "").trim(),
    _descripcion: (articulo.descripcion || "").trim(),
    _cbarras: codigo,
    _medida: articulo.medida ?? 0,
    _factor: "1",
    _cantidad: "1",
    _usuario: "",
    _pendiente: true,
    _codigo: codigo,
  };
};

export default function FrmConteosEscaneo() {
  const { isLocked } = useFormTabLock("conteos_escaneo", {
    title: "Escaneo de Conteos",
  });

  const {
    zona, conteo, almacenExhibidor,
    estado, scans,
    folioInventario,
    zonas,
    catalogoArticulos,
    setFolioInventario,
    handleCambiarZona, handleCambiarConteo, handleCambiarAlmacen,
    handleEliminarScan,
    handleEliminarConteoCompleto,
    handleValidarInventarioActivo,
    handleObtenerZonas,
    recargarDatos,
    handleNuevoConteo,
    handleCrearScanDesdeArticulo,
    handleCargarCatalogoLocal,
    handleCrearScansLote,
    resolverArticulo,
  } = useConteosEscaneo();

  const inputRef = useRef(null);
  const tableContainerRef = useRef(null);
  // Ref al TextField "Filtro" del modal buscador: permite que el Alt+F1
  // global haga focus+select aunque el foco esté en el lector o la tabla.
  const filtroBuscadorRef = useRef(null);
  const cantidadAnteriorRef = useRef(0);
  const ultimoIdRef = useRef(null);
  const zonaRef = useRef(null);
  const omitirPersistenciaRef = useRef(false);
  const guardadoEnCursoRef = useRef(null);
  const storageKeyAnteriorRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [cargandoCatalogo, setCargandoCatalogo] = useState(false);
  // Progreso de la carga manual del catélogo (menú "Catálogo").
  const [progresoCatalogo, setProgresoCatalogo] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedScanPk, setSelectedScanPk] = useState(null);
  const [invActivo, setInvActivo] = useState(null);
  const [openBuscarModal, setOpenBuscarModal] = useState(false);
  const [colectorMode, setColectorMode] = useState(() => {
    try {
      return localStorage.getItem("sizesoftx_conteos_captura_local") !== "false";
    } catch {
      return true;
    }
  });
  const [codigosEscaneados, setCodigosEscaneados] = useState([]);
  const [scansPendientes, setScansPendientes] = useState([]);
  const [zonaInput, setZonaInput] = useState("");

  const storageLecturasKey = useMemo(() => {
    if (!folioInventario || !almacenExhibidor || !zona || !conteo) return null;
    return ["sizesoftx_lecturas_pendientes", folioInventario, almacenExhibidor, zona, conteo]
      .map((valor) => encodeURIComponent(String(valor).trim().toUpperCase()))
      .join("|");
  }, [folioInventario, almacenExhibidor, zona, conteo]);

  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
  }, []);

  useEffect(() => {
    if (isLocked) return;
    window.scrollTo(0, 0);
  }, [isLocked]);

  // Sin scroll general: el cabecero queda fijo y solo desplaza la tabla.
  // Bloquea el overflow del contenedor del dashboard mientras este módulo esté
  // activo, para que exista una sola barra vertical (la del detalle).
  useEffect(() => {
    if (isLocked) return;
    const el = document.querySelector('.sizesoftx-dashboard .content');
    if (!el) return;
    const prevY = el.style.overflowY;
    const prevX = el.style.overflowX;
    el.style.overflowY = 'hidden';
    el.style.overflowX = 'hidden';
    return () => {
      el.style.overflowY = prevY;
      el.style.overflowX = prevX;
    };
  }, [isLocked]);

  useEffect(() => {
    if (isLocked) return;
    let cancelado = false;

    const inicializarConteos = async () => {
      const inventario = await handleValidarInventarioActivo();
      if (cancelado) return;

      if (!inventario) {
        setInvActivo(null);
        showToast(
          "warning",
          "No hay un inventario activo. Los conteos solo funcionan con un inventario activo."
        );
        return;
      }

      setInvActivo(inventario);
      handleCambiarAlmacen(inventario.almacenexhibidor || "");
      setFolioInventario(inventario.folioinventario);
      await handleObtenerZonas(
        inventario.folioinventario,
        inventario.almacenexhibidor || ""
      );
      // Apertura répida: solo caché local, sin red. El catélogo completo
      // se jala a mano con el botón "Catálogo" del menú.
      await handleCargarCatalogoLocal(inventario.folioinventario, { soloCache: true });

      try {
        const filtros = JSON.parse(
          localStorage.getItem(`sizesoftx_conteos_filtros_${inventario.folioinventario}`) || "{}"
        );
        if (filtros.zona) handleCambiarZona(String(filtros.zona).toUpperCase());
        if (["CONTEO 1", "CONTEO 2", "CONTEO 3"].includes(filtros.conteo)) {
          handleCambiarConteo(filtros.conteo);
        }
      } catch { }
    };

    inicializarConteos();

    return () => {
      cancelado = true;
    };
  }, [isLocked, handleValidarInventarioActivo, handleCambiarAlmacen, setFolioInventario,
    handleObtenerZonas, handleCargarCatalogoLocal, handleCambiarZona, handleCambiarConteo]);

  useEffect(() => {
    setZonaInput(zona || "");
  }, [zona]);

  useEffect(() => {
    if (!folioInventario) return;
    try {
      localStorage.setItem(
        `sizesoftx_conteos_filtros_${folioInventario}`,
        JSON.stringify({ zona, conteo, almacenExhibidor })
      );
    } catch { }
  }, [folioInventario, zona, conteo, almacenExhibidor]);

  useEffect(() => {
    omitirPersistenciaRef.current = true;
    if (!storageLecturasKey) {
      setCodigosEscaneados([]);
      setScansPendientes([]);
      // Sin llave no hay nada que omitir después: el próximo guardado
      // (nueva zona) debe persistir desde el primer escaneo.
      omitirPersistenciaRef.current = false;
      return;
    }
    try {
      const raw = localStorage.getItem(storageLecturasKey);
      const pendientes = JSON.parse(raw || "[]");
      const items = Array.isArray(pendientes) ? pendientes : [];

      // Normalizar: si son strings (formato legacy) convertir a objetos
      const codigos = items.map((item) => {
        if (typeof item === "string") return { idUnico: null, codigo: item };
        return item;
      });
      setCodigosEscaneados(codigos);

      const objetosPendientes = codigos
        .map((item) => {
          const articulo = resolverArticulo(item.codigo);
          if (!articulo) return null;
          const obj = crearObjetoScanPendiente(articulo);
          if (item.idUnico) obj._idUnico = item.idUnico;
          return obj;
        })
        .filter(Boolean);
      setScansPendientes(objetosPendientes);
    } catch {
      setCodigosEscaneados([]);
      setScansPendientes([]);
    }
  }, [storageLecturasKey]);

  useEffect(() => {
    if (!storageLecturasKey) return;

    // Limpiar llave anterior si cambió (zona, conteo, etc.)
    if (storageKeyAnteriorRef.current && storageKeyAnteriorRef.current !== storageLecturasKey) {
      try { localStorage.removeItem(storageKeyAnteriorRef.current); } catch { }
    }
    storageKeyAnteriorRef.current = storageLecturasKey;

    if (omitirPersistenciaRef.current) {
      omitirPersistenciaRef.current = false;
      return;
    }
    try {
      if (codigosEscaneados.length > 0) {
        const aGuardar = codigosEscaneados.map((item) => ({
          idUnico: item.idUnico,
          codigo: item.codigo,
        }));
        localStorage.setItem(storageLecturasKey, JSON.stringify(aGuardar));
      } else {
        localStorage.removeItem(storageLecturasKey);
      }
    } catch { }
  }, [storageLecturasKey, codigosEscaneados]);

  useEffect(() => {
    if (isLocked || !invActivo || !zona.trim() || !conteo.trim() || !almacenExhibidor.trim()) return;
    recargarDatos();
  }, [isLocked, invActivo, zona, conteo, almacenExhibidor, recargarDatos]);

  const handleConfirmDialog = async (confirmado) => {
    const action = confirmAction;
    setConfirmAction(null);
    if (!confirmado || !action) return;

    if (action.type === "eliminar-partida-pendiente") {
      setScansPendientes((prev) => prev.filter((s) => s._idUnico !== action.idUnico));
      setCodigosEscaneados((prev) => prev.filter((item) => item.idUnico !== action.idUnico));
      setSelectedScanPk(null);
      // Guardar el resto de lecturas pendientes y recargar
      setBusy(true);
      try {
        await guardarLecturasPendientes();
        await recargarDatos();
        showToast("success", "Lectura pendiente eliminada.");
      } finally {
        setBusy(false);
      }
      return;
    }

    setBusy(true);
    try {
      if (action.type === "eliminar-partida") {
        // Eliminar solo UNA ocurrencia de pendientes locales si existe (la primera coincidencia)
        if (action.cbarras) {
          setCodigosEscaneados((prev) => {
            const idx = prev.findIndex((item) => item.codigo === action.cbarras);
            if (idx === -1) return prev;
            return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
          });
          setScansPendientes((prev) => {
            const idx = prev.findIndex((s) => s._cbarras === action.cbarras);
            if (idx === -1) return prev;
            return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
          });
        }
        // Luego eliminar en backend
        const eliminado = await handleEliminarScan(action.pk);
        if (eliminado) {
          setSelectedScanPk(null);
          showToast("success", "Lectura eliminada correctamente.");
        }
        return;
      }

      if (action.type === "eliminar-conteo-completo") {
        // Limpiar localStorage de forma directa (sin esperar el efecto)
        if (storageLecturasKey) {
          try { localStorage.removeItem(storageLecturasKey); } catch { }
        }
        setScansPendientes([]);
        setCodigosEscaneados([]);
        setSelectedScanPk(null);
        // Eliminar en servidor
        await handleEliminarConteoCompleto();
        await recargarDatos();
        showToast("success", "Conteo eliminado completamente.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleArticuloSeleccionadoBuscar = async (articulo) => {
    if (!articulo) return;
    setBusy(true);
    try {
      await handleCrearScanDesdeArticulo(articulo);
    } finally {
      setBusy(false);
      setOpenBuscarModal(false);
    }
  };

  const handleColectorScan = async (codigo) => {
    if (!codigo) return;

    // Sin catélogo cargado (carga manual con el botón "Catálogo") no se
    // puede resolver: se orienta en vez de marcar "no pertenece".
    if (catalogoArticulos.length === 0) {
      showToast("warning", "Catálogo vacío. Pulsa Catálogo en el menú para cargarlo y luego escanea.");
      return;
    }

    const articulo = resolverArticulo(codigo);

    if (!articulo) {
      showToast("warning", `El código ${codigo} no pertenece al inventario activo.`);
      return;
    }

    const codigoOficial = articulo.codigobarras.trim();

    playBeep(800, 120);

    if (colectorMode) {
      const scanObj = crearObjetoScanPendiente(articulo);
      setScansPendientes((prev) => [...prev, scanObj]);
      setCodigosEscaneados((prev) => [...prev, { idUnico: scanObj._idUnico, codigo: codigoOficial }]);
    } else {
      setBusy(true);
      try {
        await handleCrearScanDesdeArticulo(articulo);
        showToast("success", "Lectura registrada.");
      } catch {
        showToast("error", "Error al registrar lectura.");
      } finally {
        setBusy(false);
      }
    }
  };

  const guardarLecturasPendientes = useCallback(async () => {
    if (!codigosEscaneados.length) return true;
    if (guardadoEnCursoRef.current) return guardadoEnCursoRef.current;

    const codigosAGuardar = codigosEscaneados.map((item) => item.codigo);
    const operacion = (async () => {
      setBusy(true);
      try {
        const resultado = await handleCrearScansLote(codigosAGuardar);
        if (!resultado) return false;

        const noEncontrados = resultado.codigosNoEncontrados || [];
        setCodigosEscaneados(noEncontrados);
        setScansPendientes([]);
        const registrados = resultado.registrados || 0;
        showToast(noEncontrados.length > 0 ? "warning" : "success",
          `${registrados} lectura(s) guardada(s)`
          + (noEncontrados.length > 0 ? `, ${noEncontrados.length} pendiente(s)` : ""));
        return noEncontrados.length === 0;
      } finally {
        setBusy(false);
        guardadoEnCursoRef.current = null;
      }
    })();

    guardadoEnCursoRef.current = operacion;
    return operacion;
  }, [codigosEscaneados, handleCrearScansLote, showToast]);

  const cambiarZona = useCallback(async (nuevaZona) => {
    const valor = String(nuevaZona || "").trim().toUpperCase();
    if (valor === zona) return true;
    if (!(await guardarLecturasPendientes())) {
      setZonaInput(zona);
      return false;
    }
    handleCambiarZona(valor);
    return true;
  }, [zona, guardarLecturasPendientes, handleCambiarZona]);

  const cambiarConteo = useCallback(async (nuevoConteo) => {
    if (!nuevoConteo || nuevoConteo === conteo) return true;
    if (!(await guardarLecturasPendientes())) return false;
    handleCambiarConteo(nuevoConteo);
    return true;
  }, [conteo, guardarLecturasPendientes, handleCambiarConteo]);

  // Carga manual del catélogo completo con barra de % (menú "Catálogo").
  // Fases: descarga -> procesando -> indexando -> guardando -> listo.
  const handleCargarCatalogoManual = useCallback(async () => {
    if (!folioInventario) {
      showToast("warning", "Sin folio de inventario activo.");
      return [];
    }
    if (cargandoCatalogo) return [];
    setCargandoCatalogo(true);
    setProgresoCatalogo({ fase: "descarga", porcentaje: 0, texto: "Iniciando descarga..." });
    try {
      const catalogo = await handleCargarCatalogoLocal(folioInventario, {
        forceRefresh: true,
        onProgress: ({ fase, porcentaje, detalle }) => {
          setProgresoCatalogo({
            fase,
            porcentaje: porcentaje ?? null,
            texto: `${nombreFaseCatalogo(fase)}${detalle ? ` — ${detalle}` : ""}`,
          });
        },
      });
      if (!catalogo || catalogo.length === 0) {
        showToast("warning", "El catélogo volvió vacío. Verifica el detalle de la toma.");
        return [];
      }
      showToast("success", `Catálogo cargado: ${catalogo.length} artículos listos para escaneo.`);
      return catalogo;
    } catch {
      showToast("error", "Error al cargar el catélogo.");
      return [];
    } finally {
      setCargandoCatalogo(false);
      // Dejar visible el 100% un momento antes de ocultar la barra.
      setTimeout(() => setProgresoCatalogo(null), 1500);
    }
  }, [folioInventario, cargandoCatalogo, handleCargarCatalogoLocal, showToast]);

  const cambiarModoCapturaLocal = useCallback(async (activo) => {
    if (!activo && !(await guardarLecturasPendientes())) return;
    if (activo && catalogoArticulos.length === 0) {
      const catalogo = await handleCargarCatalogoManual();
      if (catalogo.length === 0) {
        return;
      }
    }

    try {
      localStorage.setItem("sizesoftx_conteos_captura_local", activo ? "true" : "false");
    } catch { }
    setColectorMode(activo);
  }, [guardarLecturasPendientes, catalogoArticulos.length, handleCargarCatalogoManual]);

  useEffect(() => {
    const advertirPendientes = (event) => {
      if (codigosEscaneados.length === 0) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", advertirPendientes);
    return () => window.removeEventListener("beforeunload", advertirPendientes);
  }, [codigosEscaneados.length]);

  // Alt+F1 global del módulo:
  //  - modal cerrado → abre el buscador
  //  - modal abierto (repetir Alt+F1) → focus+select al TextField de filtro
  // Handler único en capture para no depender del orden con otros listeners.
  useEffect(() => {
    const esAltF1 = (event) => {
      if (!event.altKey) return false;
      const key = (event.key || "").trim().toUpperCase();
      return key === "F1" || event.code === "F1" || event.keyCode === 112;
    };
    const enfocarFiltroBuscador = () => {
      const el = filtroBuscadorRef.current;
      if (!el) return false;
      el.focus();
      el.select?.();
      return true;
    };
    const handleKeyDown = (event) => {
      if (!esAltF1(event)) return;
      if (!folioInventario) return;
      event.preventDefault();
      event.stopPropagation();
      if (!openBuscarModal) {
        setOpenBuscarModal(true);
        return;
      }
      // Modal ya abierto: repetir Alt+F1 siempre regresa al filtro.
      if (enfocarFiltroBuscador()) return;
      // El input del portal puede montar tarde: reintentar.
      let intentos = 0;
      const timer = setInterval(() => {
        if (enfocarFiltroBuscador() || ++intentos > 10) clearInterval(timer);
      }, 50);
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [folioInventario, openBuscarModal, setOpenBuscarModal]);

  // "Nuevo" desde el menú: limpia selección y delega al contexto, que
  // conserva folio/almacén/zonas/catélogo. Los pendientes locales se
  // limpian solos al quedar la llave de lecturas en null (zona vacía).
  const handleNuevo = useCallback(() => {
    setSelectedScanPk(null);
    handleNuevoConteo();
  }, [handleNuevoConteo]);

  const handleOnclickZonaInput = () => {
    zonaRef.current?.focus();
    zonaRef.current?.select();
  };

  const datosTabla = useMemo(() => {
    const servidor = scans.map((s) => ({
      ...s,
      _id: s.pk,
      _folio: s.folioconteo || s.FolioConteo || "",
      _clave: s.claveArticulo || s.clavearticulo?.trim() || "",
      _descripcion: s.descripcion?.trim() || s.Descripcion?.trim() || "",
      _cbarras: s.codigoBarras || s.codigobarras || s.CodigoBarras || "",
      _medida: s.medida ?? s.Medida ?? "",
      _factor: s.factor ?? s.Factor ?? "1",
      _cantidad: s.cantidad ?? s.Cantidad ?? "1",
      _usuario: s.usuario || s.Usuario || "",
      _pk: s.pk,
      _pendiente: false,
    }));
    const pendientes = scansPendientes.map((s) => ({ ...s, _id: s._temppk }));
    return [...servidor, ...pendientes];
  }, [scans, scansPendientes]);

  const totalEscaneos = datosTabla.length;

  // Lleva una fila a la vista dentro del contenedor, respetando el thead fijo.
  // Usa getBoundingClientRect (coordenadas de viewport): offsetTop es relativo
  // al offsetParent y NO es comparable con scrollTop del contenedor.
  const desplazarFilaVisible = useCallback((contenedor, fila) => {
    if (!contenedor || !fila) return;
    const rectCont = contenedor.getBoundingClientRect();
    const rectFila = fila.getBoundingClientRect();
    const altoEncabezado = contenedor.querySelector("thead")?.getBoundingClientRect().height || 0;
    if (rectFila.bottom > rectCont.bottom) {
      contenedor.scrollTop += rectFila.bottom - rectCont.bottom;
    } else if (rectFila.top < rectCont.top + altoEncabezado) {
      contenedor.scrollTop -= (rectCont.top + altoEncabezado) - rectFila.top;
    }
  }, []);

  useEffect(() => {
    const cantidadActual = datosTabla.length;
    if (cantidadActual === 0) {
      cantidadAnteriorRef.current = 0;
      ultimoIdRef.current = null;
      setSelectedScanPk(null);
      return;
    }
    const ultimoRegistro = datosTabla[cantidadActual - 1];
    const esNuevo = cantidadActual > cantidadAnteriorRef.current
      || ultimoIdRef.current !== (ultimoRegistro?._id || null);
    if (esNuevo) {
      ultimoIdRef.current = ultimoRegistro?._id || null;
      setSelectedScanPk(ultimoRegistro?._id || null);
      // El último registro siempre queda a la vista bajo el encabezado fijo
      requestAnimationFrame(() => {
        const contenedor = tableContainerRef.current;
        const fila = contenedor?.querySelector('[data-last-row="true"]')
          ?? contenedor?.querySelector(`[data-row-index="${cantidadActual - 1}"]`);
        if (fila) desplazarFilaVisible(contenedor, fila);
        else if (contenedor) contenedor.scrollTop = contenedor.scrollHeight;
      });
    }
    cantidadAnteriorRef.current = cantidadActual;
  }, [datosTabla, desplazarFilaVisible]);

  const handleNavegarTabla = useCallback((event) => {
    if (!datosTabla.length || !["ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();

    const indiceActual = datosTabla.findIndex((registro) => registro._id === selectedScanPk);
    const desplazamiento = event.key === "ArrowDown" ? 1 : -1;
    const indiceBase = indiceActual < 0 ? (desplazamiento > 0 ? -1 : datosTabla.length) : indiceActual;
    const nuevoIndice = Math.min(Math.max(indiceBase + desplazamiento, 0), datosTabla.length - 1);
    const nuevoRegistro = datosTabla[nuevoIndice];
    setSelectedScanPk(nuevoRegistro._id);

    requestAnimationFrame(() => {
      const contenedor = tableContainerRef.current;
      const fila = contenedor?.querySelector(`[data-row-index="${nuevoIndice}"]`);
      desplazarFilaVisible(contenedor, fila);
    });
  }, [datosTabla, selectedScanPk, desplazarFilaVisible]);

  const handleEliminarPendiente = (idUnico) => {
    setConfirmAction({
      type: "eliminar-partida-pendiente",
      idUnico,
    });
  };

  return (
    <Fragment>
      <SizeSoftXLayoutForm
        titlePage="Escaneo de Conteos"
        buttonActions={
            <MnuConteosEscaneoActions
                selectedScanPk={selectedScanPk}
                hasActiveConteo={!!conteo && !!almacenExhibidor && (scans.length > 0 || codigosEscaneados.length > 0)}
                onConfirmEliminarPartida={() => setConfirmAction({
                    type: "eliminar-partida",
                    pk: selectedScanPk,
                })}
                onConfirmEliminarCompleto={() => setConfirmAction({
                    type: "eliminar-conteo-completo",
                })}
                onNuevoConteo={handleNuevo}
                onBuscar={() => setOpenBuscarModal(true)}
                onCargarCatalogo={handleCargarCatalogoManual}
                cargandoCatalogo={cargandoCatalogo}
                storageLecturasKey={storageLecturasKey}
                onGuardar={guardarLecturasPendientes}
            />
        }
      >
        <Box sx={{ p: { xs: 1, md: 1.5 }, width: "100%", boxSizing: "border-box" }}>
          {!invActivo ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              No hay un inventario activo. Los conteos requieren un inventario activo.
            </Typography>
          ) : (
            <Fragment>
              <Paper elevation={2} sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
                <Grid container spacing={1.5} alignItems="center" sx={{ width: "100%", m: 0 }}>
                  <Grid sx={{ flex: "1 1 220px", minWidth: { xs: "100%", sm: 220 } }}>
                    <Autocomplete
                        freeSolo
                        autoSelect
                        fullWidth
                        options={zonas}
                        value={zona || null}
                        inputValue={zonaInput}
                        onChange={async (event, value) => {
                          const cambiado = await cambiarZona(value ?? "");
                          if (cambiado) setZonaInput(String(value || "").toUpperCase());
                        }}
                        onInputChange={(event, value, reason) => {
                          if (reason === "input" || reason === "clear") {
                            setZonaInput(String(value || "").toUpperCase());
                          }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                ref={zonaRef}
                                label="Zona"
                                size="small"
                                onKeyDown={async (e) => {
                                    if (e.key !== "Enter") return;
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const cambiado = await cambiarZona(zonaInput);
                                    if (cambiado) {
                                        setZonaInput(zonaInput.toUpperCase());
                                        inputRef.current?.focus();
                                        inputRef.current?.select();
                                    }
                                }}
                            />
                        )}
                        onClick={handleOnclickZonaInput}
                    />
                  </Grid>
                  <Grid sx={{ flex: "1 1 220px", minWidth: { xs: "100%", sm: 220 } }}>
                    <Autocomplete
                      options={["CONTEO 1", "CONTEO 2", "CONTEO 3"]}
                      value={conteo}
                      onChange={(e, val) => val && cambiarConteo(val)}
                      renderInput={(params) => (
                        <TextField {...params} label="Conteo" size="small" />
                      )}
                      disableClearable
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid sx={{ flex: "2 1 320px", minWidth: { xs: "100%", sm: 320 } }}>
                    <TextField autoComplete="off"
                      fullWidth
                      label="Almacen / Exhibidor"
                      value={almacenExhibidor || invActivo?.almacenexhibidor || ""}
                      size="small"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid sx={{ flex: "0 1 110px", minWidth: 100 }}>
                    <Chip
                      label={estado}
                      color={estado === "ABIERTO" ? "success" : "error"}
                      size="small"
                      sx={{ fontWeight: 800 }}
                    />
                  </Grid>
                  <Grid sx={{ flex: "0 1 150px", minWidth: 140 }}>
                    <Chip
                      label={`Total artículos: ${catalogoArticulos.length}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  </Grid>
                  <Grid sx={{ flex: "0 1 80px", minWidth: 70 }}>
                    <Typography variant="caption" color="text.secondary">
                      Inv: <strong>{folioInventario ?? "-"}</strong>
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                elevation={3}
                sx={{
                  p: { xs: 1, md: 1.5 },
                  borderRadius: 2,
                  width: "100%",
                  minHeight: "calc(100vh - 250px)",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                  {progresoCatalogo && (
                    <Box sx={{ width: "100%", px: 0.5, py: 0.5, border: "1px solid", borderColor: "info.main", borderRadius: 1.5, bgcolor: "info.light" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {progresoCatalogo.texto || "Cargando catélogo..."}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {(progresoCatalogo.porcentaje ?? null) === null ? "" : `${progresoCatalogo.porcentaje}%`}
                        </Typography>
                      </Box>
                      {progresoCatalogo.porcentaje === null || progresoCatalogo.porcentaje === undefined ? (
                        <LinearProgress sx={{ height: 8, borderRadius: 2 }} />
                      ) : (
                        <LinearProgress variant="determinate" value={progresoCatalogo.porcentaje} sx={{ height: 8, borderRadius: 2 }} />
                      )}
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      gap: 1.5,
                      px: 0.5,
                    }}
                  >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 3, cursor: "pointer", fontSize: 11, whiteSpace: "nowrap" }}>
                      <input
                        type="checkbox"
                        checked={colectorMode}
                        onChange={(e) => cambiarModoCapturaLocal(e.target.checked)}
                        disabled={busy || cargandoCatalogo}
                      />
                      Captura local
                    </label>
                    <Chip label={`Total escaneos: ${totalEscaneos}`} size="small" color="info" sx={{ fontWeight: 700 }} />
                    {scansPendientes.length > 0 && (
                      <Chip label={`${scansPendientes.length} pendiente(s)`} color="warning" size="small" sx={{ fontWeight: 600 }} />
                    )}
                  </Box>
                  <Box
                    className="framelectorcodigosbarras"
                    sx={{
                      m: 0,
                      p: 0,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 0.5,
                      flex: "0 1 auto",
                      minWidth: 300,
                      maxWidth: 480,
                      "& .framedetallesventa": {
                        m: 0,
                        p: 0,
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                        display: "flex",
                        alignItems: "center",
                      },
                      "& .framedetallesventaLabel": { display: "none" },
                      "& .txtCodigoBarras": {
                        width: "100%",
                        maxWidth: "none",
                        boxSizing: "border-box",
                        height: 34,
                      },
                      "& .camara-codigo-barras-portatil": {
                        flex: "0 0 auto",
                        m: 0,
                      },
                      "& .camara-codigo-barras-portatil__toggle": {
                        width: "auto",
                        height: 34,
                        px: 1,
                        fontSize: "0.78rem",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <SizeSoftXLectorCodigoBarrasConteos
                      ref={inputRef}
                      disabled={busy}
                      placeholder="Escanea o escribe codigo"
                      onColectorScan={handleColectorScan}
                    />
                    <SizeSoftXCodigoBarrasCamaraConteos />
                  </Box>
                </Box>

                <TableContainer
                  ref={tableContainerRef}
                  component={Box}
                  tabIndex={0}
                  role="grid"
                  data-conteos-table="true"
                  aria-label="Registros escaneados; navega con flecha arriba y flecha abajo"
                  onKeyDown={handleNavegarTabla}
                  sx={{
                    flex: 1,
                    minHeight: 360,
                    height: "calc(100vh - 330px)",
                    overflowX: "auto",
                    // Slider vertical siempre visible (aunque quepan las filas)
                    // para ver y arrastrar los escaneos sin depender del touchpad.
                    overflowY: "scroll",
                    // Scroll con rueda y con 2 dedos del touchpad / téctil:
                    // no bloquear gestos; el scroll nativo del contenedor manda.
                    WebkitOverflowScrolling: "touch",
                    overscrollBehavior: "contain",
                    touchAction: "pan-x pan-y",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                    bgcolor: "background.paper",
                    scrollbarGutter: "stable",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#1976d2 #e0e0e0",
                    "&::-webkit-scrollbar": { width: 12, height: 12 },
                    "&::-webkit-scrollbar-track": { background: "#e0e0e0", borderRadius: 8 },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#1976d2",
                      borderRadius: 8,
                      border: "2px solid #e0e0e0",
                    },
                    "&::-webkit-scrollbar-thumb:hover": { background: "#115293" },
                  }}
                >
                  <Table stickyHeader size="small" sx={{ minWidth: 950, tableLayout: "fixed" }}>
                    <TableHead>
                      <TableRow>
                        {[
                          ["Descripción", 260], ["Medida", 75], ["Cantidad", 85], ["Usuario", 105],
                          ["C. Barras", 150], ["Clave", 120], ["#", 65], ["Factor", 75], ["Acción", 70],
                        ].map(([titulo, ancho]) => (
                          <TableCell
                            key={titulo}
                            align={["Medida", "Cantidad", "Factor", "Acción"].includes(titulo) ? "center" : "left"}
                            sx={{
                              width: ancho,
                              minWidth: ancho,
                              py: 1,
                              px: 1,
                              bgcolor: "primary.main",
                              color: "primary.contrastText",
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {titulo}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {datosTabla.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 5, color: "text.secondary" }}>
                            No hay escaneos registrados
                          </TableCell>
                        </TableRow>
                      ) : datosTabla.map((r, index) => {
                        const seleccionado = selectedScanPk === r._id;
                        return (
                          <TableRow
                            key={r._id}
                            hover
                            selected={seleccionado}
                            onClick={() => {
                              setSelectedScanPk(r._id);
                              tableContainerRef.current?.focus();
                            }}
                            data-row-index={index}
                            data-last-row={index === datosTabla.length - 1 ? "true" : undefined}
                            sx={{
                              cursor: "pointer",
                              "&:nth-of-type(even)": { bgcolor: seleccionado ? undefined : "action.hover" },
                              "&.Mui-selected": {
                                bgcolor: "primary.light",
                                "&:hover": { bgcolor: "primary.light" },
                              },
                            }}
                          >
                            <TableCell sx={{ py: 0.5, px: 1 }}><Typography variant="body2" noWrap title={r._descripcion}>{r._descripcion}</Typography></TableCell>
                            <TableCell align="center" sx={{ py: 0.5, px: 1 }}>{r._medida}</TableCell>
                            <TableCell align="center" sx={{ py: 0.5, px: 1, fontWeight: 700 }}>{r._cantidad}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 1 }}><Typography variant="body2" noWrap>{r._usuario}</Typography></TableCell>
                            <TableCell sx={{ py: 0.5, px: 1 }}><Typography variant="body2" noWrap>{r._cbarras}</Typography></TableCell>
                            <TableCell sx={{ py: 0.5, px: 1 }}><Typography variant="body2" noWrap>{r._clave}</Typography></TableCell>
                            <TableCell sx={{ py: 0.5, px: 1 }}>{r._folio}</TableCell>
                            <TableCell align="center" sx={{ py: 0.5, px: 1 }}>{r._factor}</TableCell>
                            <TableCell align="center" sx={{ py: 0.25, px: 0.5 }}>
                              <Tooltip title={r._pendiente ? "Eliminar lectura pendiente" : "Eliminar captura"}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            if (r._pendiente) {
                              handleEliminarPendiente(r._idUnico);
                            } else {
                              setConfirmAction({ type: "eliminar-partida", pk: r._pk, cbarras: r._cbarras });
                            }
                          }}
                      disabled={false}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Fragment>
          )}
        </Box>
      </SizeSoftXLayoutForm>

      <SizeSoftXAlert />

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
      >
        {toast ? (
          <Alert severity={toast.type} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : null}
      </Snackbar>

      <ModalBuscarArticulosTomaInventario
        openModal={openBuscarModal}
        handleCloseModal={() => setOpenBuscarModal(false)}
        handleArticuloSelect={handleArticuloSeleccionadoBuscar}
        folioInventario={folioInventario}
        inputRefExterno={filtroBuscadorRef}
      />

      <SizeSoftXConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.type === "eliminar-conteo-completo"
          ? "Eliminar conteo completo"
          : "Eliminar lectura"}
        message={confirmAction?.type === "eliminar-conteo-completo"
          ? "Se eliminarán todas las lecturas de la zona, conteo y almacén seleccionados. Esta acción no se puede deshacer. ¿Deseas continuar?"
          : "Se eliminará la lectura seleccionada. Esta acción no se puede deshacer. ¿Deseas continuar?"}
        handleConfirmDialog={handleConfirmDialog}
        colorTitle="#d32f2f"
      />


    </Fragment>
  );
}
